import dayjs from 'dayjs'
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'

import { usePreferencesStore, PreferenceKey } from '@/entities/preference'

import { getProfileTimezone } from '@/shared/composables/timezone'
import { resolveTimezone } from '@/shared/lib/resolve-timezone'

import { TIMEZONES } from './constants'

const TIMEZONE_LOCALSTORAGE_KEY = 'timezone'

export { getProfileTimezone } from '@/shared/composables/timezone'

export const useTimezoneSelector = () => {
  const preferencesStore = usePreferencesStore()
  const { preferences, isLoaded } = storeToRefs(preferencesStore)
  const { setPreferences } = preferencesStore

  const currentTimezone = computed({
    get: () => getProfileTimezone(),
    set: (tz: string) => {
      const resolvedTimezone = resolveTimezone(tz)
      setPreferences(PreferenceKey.CURRENT_TIME_ZONE, resolvedTimezone)
      window.localStorage.setItem(TIMEZONE_LOCALSTORAGE_KEY, resolvedTimezone)
      dayjs.tz.setDefault(resolvedTimezone)
    },
  })

  watch(preferences, (preferencesNew) => {
    const rawTimezone = preferencesNew?.[PreferenceKey.CURRENT_TIME_ZONE] as string | undefined
    const resolvedTimezone = resolveTimezone(rawTimezone ?? window.localStorage.getItem(TIMEZONE_LOCALSTORAGE_KEY))

    if (rawTimezone && resolvedTimezone !== rawTimezone) {
      currentTimezone.value = resolvedTimezone
      return
    }

    dayjs.tz.setDefault(resolvedTimezone)
    window.localStorage.setItem(TIMEZONE_LOCALSTORAGE_KEY, resolvedTimezone)
  }, { deep: true })

  watch(isLoaded, (loaded) => {
    if (loaded) {
      refreshTzSettings()
    }
  })

  const currentTimezoneOffset = computed(() => {
    return TIMEZONES.find((item) => item.value === currentTimezone.value)?.label
  })

  const refreshTzSettings = () => {
    dayjs.tz.setDefault(getProfileTimezone())
  }

  return {
    currentTimezone,
    currentTimezoneOffset,
    refreshTzSettings,
  }
}
